import React from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';

import * as ErrorMessages from '../constants/errorMessages';
import * as dg from './dx/dataGridHelper';

export const isEndState = exam => {
    return exam && ['Completed'].some(s => s === exam.status);
}

export const displayStartExamError = (error) => {
    if (error && error.response && error.response.status) {
        switch (error.response.status.toString()) {
            case '401':
                toast.error(() => <p><strong>Cannot start someone else's exam</strong>.</p>, { position: 'top-center', style: { width: '380px' } });
                return;

            case '404':
                toast.error(() => <p><strong>Cannot start exam</strong>.  Exam template not found.</p>, { position: 'top-center', style: { width: '380px' } });
                return;

            default:
                break;
        }
    }

    toast.error(() => ErrorMessages.GENERIC_FORM_ERROR_HTML, { position: 'top-center', style: { width: '380px' } });
}

export const getDefaultService = (services) => {
    if (!services || services.length === 0) return null;

    return services.slice().sort((a, b) => {
        if (a.priority > b.priority) {
            return -1;
        }
        else if (a.priority < b.priority) {
            return 1;
        }
        else {
            if (a.examTemplateId && !b.examTemplateId) {
                return -1;
            }
            else if (!a.examTemplateId && b.examTemplateId) {
                return 1;
            }
            else {
                return a.code.localeCompare(b.code);
            }
        }
    })[0];
}

export const getDefaultExamTemplateId = (services) => {
    if (!services || services.length === 0) return null;

    const defaultService = getDefaultService(services);
    let examTemplateId = null;

    if (defaultService.examTemplateId) {
        examTemplateId = defaultService.examTemplateId;
    }
    else if (services.slice().some(s => s.examTemplateId)) {
        examTemplateId = services.slice().filter(s => s.examTemplateId).sort((a, b) => {
            if (a.priority > b.priority) {
                return -1;
            }
            else if (a.priority < b.priority) {
                return 1;
            }
            else {
                return a.code.localeCompare(b.code);
            }
        })[0].examTemplateId;
    }

    return examTemplateId;
}

export const getStatusFilters = () => {
    return [
        {
            text: getStatusDescription('NotStarted'),
            value: 'NotStarted'
        },
        {
            text: getStatusDescription('InProgress'),
            value: 'InProgress'
        },

        {
            text: getStatusDescription('Completed'),
            value: 'Completed'
        },
    ];
}

export const getStatusDescription = (status) => {
    if (!status) return;

    switch (status) {
        case 'NotStarted':
            return 'Not started';

        case 'InProgress':
            return 'In progress';

        case 'Completed':
            return 'Completed';

        default:
            return;
    }
}

export const getQuickFilterDescription = (type) => {
    if (type) {
        switch (type) {
            case 'TodayExams':
                return `Today exams`;

            case 'YesterdayExams':
                return `Yesterday exams`;

            case 'TomorrowExams':
                return `Tomorrow exams`;

            case 'ThisWeekExams':
                return `This week exams`;

            case 'LastWeekExams':
                return `Last week exams`;

            case 'NextWeekExams':
                return `Next week exams`;

            case 'LastMonthExams':
                return `${moment().add(-1, 'months').format('MMMM YYYY')} exams`;

            case 'ThisMonthExams':
                return `${moment().format('MMMM YYYY')} exams`;

            case 'NextMonthExams':
                return `${moment().add(1, 'months').format('MMMM YYYY')} exams`;

            case 'IncompleteExams':
                return `Incomplete exams`;

            case 'RecentAutoCompletedExams':
                return `Recent auto completed Exams`;

            case 'RecentExamsIncompleteReferrals':
                return `Recent incomplete referrals`;

            case 'RecentExamsIncompleteMedicalReports':
                return `Recent incomplete medical reports`;

            default:
                return null;
        }
    }
    return null;
}

export const getQuickFilterDefinition = (type) => {
    if (type) {
        const statusFilters = getStatusFilters();
        const scheduledStartFilters = getScheduledStartFilters();

        switch (type) {
            case 'TodayExams':
                return [{
                    column: 'ScheduledStart',
                    headerFilters: [scheduledStartFilters.filter(u => u.text === 'Today')[0].value],
                    sortOrder: 'asc'
                }];

            case 'YesterdayExams':
                return [{
                    column: 'ScheduledStart',
                    headerFilters: [scheduledStartFilters.filter(u => u.text === 'Yesterday')[0].value],
                    sortOrder: 'asc'
                }];

            case 'TomorrowExams':
                return [{
                    column: 'ScheduledStart',
                    headerFilters: [scheduledStartFilters.filter(u => u.text === 'Tomorrow')[0].value],
                    sortOrder: 'asc'
                }];

            case 'ThisWeekExams':
                return [{
                    column: 'ScheduledStart',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('week').startOf('day').toDate(), moment().endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }];

            case 'LastWeekExams':
                return [{
                    column: 'ScheduledStart',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'weeks').startOf('week').startOf('day').toDate(), moment().add(-1, 'weeks').endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }];

            case 'NextWeekExams':
                return [{
                    column: 'ScheduledStart',
                    filter: {
                        operator: 'between',
                        value: [moment().add(1, 'weeks').startOf('week').startOf('day').toDate(), moment().add(1, 'weeks').endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }];

            case 'LastMonthExams':
                return [{
                    column: 'ScheduledStart',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'months').startOf('week').startOf('day').toDate(), moment().add(-1, 'months').endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }];

            case 'ThisMonthExams':
                return [{
                    column: 'ScheduledStart',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('months').startOf('day').toDate(), moment().endOf('months').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }];

            case 'NextMonthExams':
                return [{
                    column: 'ScheduledStart',
                    filter: {
                        operator: 'between',
                        value: [moment().add(1, 'months').startOf('week').startOf('day').toDate(), moment().add(1, 'months').endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }];

            case 'IncompleteExams':
                return [{
                    column: 'ScheduledStart',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'In progress')[0].value,
                    ],
                }]

            case 'RecentAutoCompletedExams':
                return [{
                    column: 'ScheduledStart',
                    sortOrder: 'asc'
                }, {
                    column: 'CompletedDateUtc',
                    filter: {
                        operator: '>',
                        value: moment().add(-90, 'days').startOf('day').toDate(),
                    }
                }, {
                    column: 'CompletedBy',
                    filter: {
                        operator: '=',
                        value: 'System',
                    }
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Completed')[0].value,
                    ],
                }]

            case 'RecentExamsIncompleteReferrals':
                return [{
                    column: 'ScheduledStart',
                    headerFilters: [scheduledStartFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'HasIncompleteReferral',
                    filter: {
                        operator: '=',
                        value: true,
                    },
                }]

            case 'RecentExamsIncompleteMedicalReports':
                return [{
                    column: 'ScheduledStart',
                    headerFilters: [scheduledStartFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'HasIncompleteMedicalReport',
                    filter: {
                        operator: '=',
                        value: true,
                    },
                }]

            default:
                return;
        }
    }
}

export const getScheduledStartFilters = () => {
    return dg.getDefaultNextLastDateFilters('ScheduledStart');
}